import { animated, SpringConfig, SpringValue, useSpring, useSpringRef } from '@react-spring/web';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { Nullable } from 'src/types/nullable.type';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { BlockView } from '../block-view';
import { ACTIVE_SECTION_TOP, getActiveSectionId } from '../helpers/get-active-section-id';
import { getSuperRect } from '../helpers/get-super-rect';
import { Block, BlockRenderElement, Rect, Section } from '../types';
import { useReactSpringScrollTo } from '../utils/react-spring-scroll-to.hook';
import { usePlayerStore } from './player-store';

const DRAWER_ANIMATION_CONFIG: SpringConfig = {
  mass: 1,
  tension: 400,
  friction: 40,
};

function getHtmlText(text: string) {
  const div = document.createElement('div');
  div.innerHTML = text;
  return div.textContent || div.innerText || '';
}

type SectionCardData = {
  section: Section;
  title: Nullable<string>;
  blockRenderElements: BlockRenderElement[];
};

export const DesktopNavigationBar = () => {
  const {
    layout: { renderElements, layoutReady, sections, sectionRects, innerActualHeight },
    innerAreaHeight,
    playerContentElement,
    canvasScrollPosition,
  } = usePlayerStore();
  const [currentSectionId, setCurrentSectionId] = React.useState<Nullable<Section['id']>>(null);
  const [scrollToSectionId, setScrollToSectionId] = useState<Section['id'] | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const renderElementsMap = useMemo(() => {
    const map = new Map<Block['id'], BlockRenderElement>();
    for (const re of renderElements) {
      if (re.type === 'block') {
        map.set(re.block.id, re);
      }
    }
    return map;
  }, [renderElements]);

  const sectionCardData: SectionCardData[] = sections.map((section) => {
    let title = section.name;
    if (title === null) {
      const textBlocks = section.blocks.filter((block) => block.content.type === 'text');
      for (const block of textBlocks) {
        if (block.content.type === 'text') {
          title = getHtmlText(block.content.value);
        }
        if (title) {
          break;
        }
      }
    }
    const blockRenderElements = section.blocks
      .map((block) => renderElementsMap.get(block.id))
      .filter(Boolean) as BlockRenderElement[];

    return {
      section,
      title: title,
      blockRenderElements,
    };
  });

  const updateActiveSectionId = useRefCallback(() => {
    if (!innerAreaHeight) return null;
    if (!innerActualHeight) return null;
    const scrollBasedActiveId = getActiveSectionId({
      sections,
      sectionRects,
      innerAreaHeight,
      innerActualHeight,
      canvasScrollPosition,
    });
    if (!scrollBasedActiveId) return;
    if (scrollToSectionId) return;
    if (scrollBasedActiveId === currentSectionId) return;
    console.log('DesktopSectionDrawer: update active section', scrollBasedActiveId);
    setCurrentSectionId(scrollBasedActiveId);
  }, [
    innerAreaHeight,
    innerActualHeight,
    sections,
    sectionRects,
    canvasScrollPosition,
    currentSectionId,
    scrollToSectionId,
  ]);

  useEffect(() => {
    updateActiveSectionId();
  }, [canvasScrollPosition, innerActualHeight, innerAreaHeight, updateActiveSectionId]);

  const currentSectionIndex = sections.findIndex((section) => section.id === currentSectionId);

  const renderSectionThumbnail = (section: Section, hoverRotateClasses: string) => {
    const sectionIndex = sections.findIndex((s) => s.id === section.id);
    const sectionRect = sectionRects[sectionIndex];
    const blockRenderElements = section.blocks
      .map((block) => renderElementsMap.get(block.id))
      .filter(Boolean) as BlockRenderElement[];
    const availableWidth = 56 - 8;
    const scale = availableWidth / Math.max(sectionRect.width, 1);

    return (
      <div
        className={classNames(
          'w-14 h-[42px] bg-white rounded shrink-0 relative overflow-hidden transition origin-bottom-right',
          hoverRotateClasses
        )}
      >
        <div className='absolute inset-1 rounded overflow-hidden'>
          <div
            className='absolute origin-top-left'
            style={{
              transform: `scale(${scale})`,
              width: `${sectionRect.width}px`,
              height: `${sectionRect.height}px`,
              left: `${0}px`,
              top: `${0}px`,
            }}
          >
            <div className='absolute w-full h-full origin-center'>
              {blockRenderElements.map((re) => (
                <BlockView
                  key={re.block.id}
                  block={re.block}
                  rect={re.rect}
                  style={{
                    x: new SpringValue(re.rect.x - sectionRect.x),
                    y: new SpringValue(re.rect.y - sectionRect.y),
                    width: new SpringValue(re.rect.width),
                    height: new SpringValue(re.rect.height),
                    opacity: new SpringValue(1),
                    scale: new SpringValue(1),
                  }}
                  grabbing={false}
                  contextMenuOpen={false}
                  hasFocus={false}
                  selected={false}
                  onBlockSize={() => {}}
                  onBlur={() => {}}
                  onFocus={() => {}}
                  onDrag={() => {}}
                  onReplaceBlock={() => {}}
                  onSetVerticalAlignment={() => {}}
                  onStart={() => {}}
                  onStop={() => {}}
                  onUpdateBlockContent={() => {}}
                  selectionCoords={null}
                  setContextMenuState={() => {}}
                />
              ))}
            </div>
          </div>
        </div>
        <div className='absolute inset-0 bg-transparent' />
      </div>
    );
  };

  let firstThumbnailIndex = -1;
  let secondThumbnailIndex = -1;
  let thirdThumbnailIndex = -1;
  if (currentSectionIndex !== -1 && sectionCardData.length > 0) {
    if (sectionCardData.length === 1) {
      firstThumbnailIndex = 0;
    } else if (sectionCardData.length === 2) {
      if (currentSectionIndex === 0) {
        firstThumbnailIndex = 0;
        secondThumbnailIndex = 1;
      } else {
        firstThumbnailIndex = 1;
        secondThumbnailIndex = 0;
      }
    } else {
      if (currentSectionIndex === 0) {
        firstThumbnailIndex = 0;
        secondThumbnailIndex = firstThumbnailIndex + 1;
        thirdThumbnailIndex = firstThumbnailIndex + 2;
      } else if (currentSectionIndex === sectionCardData.length - 1) {
        firstThumbnailIndex = currentSectionIndex;
        secondThumbnailIndex = firstThumbnailIndex - 1;
        thirdThumbnailIndex = firstThumbnailIndex - 2;
      } else {
        firstThumbnailIndex = currentSectionIndex;
        secondThumbnailIndex = firstThumbnailIndex - 1;
        thirdThumbnailIndex = firstThumbnailIndex + 1;
      }
    }
  }

  const containerRef = useRef<HTMLDivElement>(null);
  useClickAway(containerRef, () => {
    setDrawerOpen(false);
  });

  const api = useSpringRef();
  const [drawerStyles] = useSpring(
    () => ({
      ref: api,
      from: { opacity: 0, y: 34 },
    }),
    []
  );

  useEffect(() => {
    if (drawerOpen) {
      api.start({ opacity: 1, y: 0, config: DRAWER_ANIMATION_CONFIG });
    } else {
      api.start({ opacity: 0, y: 34, config: DRAWER_ANIMATION_CONFIG });
    }
  }, [drawerOpen, api]);

  const { scrollTo } = useReactSpringScrollTo();

  useEffect(() => {
    if (scrollToSectionId && playerContentElement) {
      const sectionIndex = sections.findIndex((s) => s.id === currentSectionId);
      if (sectionIndex === -1) {
        return;
      }
      const sectionRect = sectionRects[sectionIndex];
      if (!sectionRect) {
        return;
      }
      let scrollY = sectionRect.y - ACTIVE_SECTION_TOP;
      scrollY = Math.max(scrollY, 0);
      scrollTo(playerContentElement, scrollY, () => {
        setScrollToSectionId(null);
      });
    }
  }, [scrollToSectionId]);

  return (
    <div
      ref={containerRef}
      className={classNames(
        'pointer-events-auto transition-opacity text-white rounded-lg flex flex-col items-center gap-2 relative',
        {
          'opacity-0': !layoutReady,
        }
      )}
    >
      <div
        className={classNames('absolute bottom-0 right-0 rounded-lg', {
          'pointer-events-none': !drawerOpen,
        })}
      >
        <animated.div
          style={drawerStyles}
          className='flex flex-col gap-1 p-1 bg-neue-glass neue-glass-backdrop-blur rounded-lg'
        >
          {sectionCardData.map(({ section, title, blockRenderElements }, i) => {
            return (
              <div
                className={classNames(
                  'w-[304px] h-[42px] flex items-center gap-2 transition-opacity cursor-pointer hover:opacity-100',
                  {
                    'opacity-70': i !== currentSectionIndex,
                    'opacity-100': i === currentSectionIndex,
                  }
                )}
                onClick={() => {
                  console.log('DesktopSectionDrawer: onClick: section.id: ', section.id);
                  setCurrentSectionId(section.id);
                  setScrollToSectionId(section.id);
                }}
              >
                <div className='ml-1 flex items-center justify-center text-bedrock-p-small shrink-0 min-w-[24px]'>
                  {i + 1}
                </div>
                {renderSectionThumbnail(section, '')}
                <div className='flex flex-1 text-bedrock-p-small items-center line-clamp-2'>{title}</div>
              </div>
            );
          })}
        </animated.div>
      </div>
      <div
        className={classNames(
          'group flex gap-2 items-center transition-opacity bg-neue-glass neue-glass-backdrop-blur rounded-lg hover:cursor-pointer p-1',
          {
            'opacity-0 pointer-events-none': drawerOpen,
            'opacity-100': !drawerOpen,
          }
        )}
        onClick={() => setDrawerOpen((open) => !open)}
      >
        <div className='min-w-[24px] flex items-center justify-center text-bedrock-p-small-strong'>
          {currentSectionIndex + 1}
        </div>
        {currentSectionIndex !== -1 && (
          <div className='relative'>
            <div className='absolute inset-0'>
              {secondThumbnailIndex !== -1 &&
                renderSectionThumbnail(sections[secondThumbnailIndex], 'group-hover:rotate-[8.4deg]')}
            </div>
            <div className='absolute inset-0'>
              {thirdThumbnailIndex !== -1 &&
                renderSectionThumbnail(sections[thirdThumbnailIndex], 'group-hover:rotate-[-9.9deg]')}
            </div>
            <div>{firstThumbnailIndex !== -1 && renderSectionThumbnail(sections[firstThumbnailIndex], '')}</div>
          </div>
        )}
      </div>
    </div>
  );
};
